import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Download, DollarSign, Package, RefreshCw, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { listAppointments } from '../api/appointments';
import { listCustomers } from '../api/customers';
import { getOrder, listOrders } from '../api/marketplace';
import { listPayments } from '../api/payments';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { currency, dateTime, downloadCsv } from '../utils/format';
import { appointmentStatus, getStatusConfig, orderStatus, paymentStatus } from '../utils/status';

const today = new Date();
const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const todayIso = today.toISOString().slice(0, 10);

const inPeriod = (value, from, to) => {
  const day = String(value || '').slice(0, 10);
  return day >= from && day <= to;
};

const statusLabel = (map, value) => getStatusConfig(map, value).label;

const reportName = (name, from, to) => `${name}-${from}-a-${to}.csv`;

const StatCard = ({ title, value, icon: Icon, tone = 'blue' }) => {
  const tones = {
    blue: 'border-blue-100 bg-blue-50 text-blue-700',
    green: 'border-green-100 bg-green-50 text-green-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    slate: 'border-gray-200 bg-white text-gray-700'
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{title}</p>
        <span className={`rounded-md border p-2 ${tones[tone] || tones.blue}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-950">{value}</p>
    </div>
  );
};

const Reportes = () => {
  const [from, setFrom] = useState(startOfMonth);
  const [to, setTo] = useState(todayIso);
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const [appointmentData, customerData, orderData, paymentData] = await Promise.all([
        listAppointments({ from: `${from} 00:00:00`, to: `${to} 23:59:59`, limit: 100 }),
        listCustomers({ limit: 100 }),
        listOrders({ limit: 100 }),
        listPayments({ from: `${from} 00:00:00`, to: `${to} 23:59:59`, limit: 100 })
      ]);

      const periodOrders = orderData.filter((order) => inPeriod(order.created_at, from, to));
      const details = await Promise.all(
        periodOrders.slice(0, 50).map((order) => getOrder(order.id).catch(() => null))
      );

      setAppointments(appointmentData);
      setCustomers(customerData);
      setOrders(periodOrders);
      setPayments(paymentData);
      setOrderDetails(details.filter(Boolean));
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar reportes'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const newCustomers = useMemo(() => {
    return customers.filter((customer) => inPeriod(customer.created_at, from, to));
  }, [customers, from, to]);

  const paidPayments = useMemo(() => {
    return payments.filter((payment) => ['paid', 'approved', 'completed'].includes(String(payment.status).toLowerCase()));
  }, [payments]);

  const totalSales = paidPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const pendingSales = payments
    .filter((payment) => String(payment.status).toLowerCase() === 'pending')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const orderTotal = orders.reduce((sum, order) => sum + Number(order.total_amount || 0), 0);

  const appointmentReportRows = useMemo(() => {
    return appointments.map((appointment) => ({
      Fecha: dateTime(appointment.starts_at),
      Cliente: appointment.customer_name || 'Sin cliente',
      Mascota: appointment.pet_name || 'Sin mascota',
      Servicio: appointment.service_name || 'Sin servicio',
      Profesional: appointment.professional_name || 'Sin asignar',
      Sucursal: appointment.branch_name || 'Sin sucursal',
      Estado: statusLabel(appointmentStatus, appointment.status)
    }));
  }, [appointments]);

  const paymentReportRows = useMemo(() => {
    return payments.map((payment) => ({
      Fecha: dateTime(payment.paid_at || payment.created_at),
      Cliente: payment.customer_name || 'Sin cliente',
      Concepto: payment.title || 'Pago',
      Estado: statusLabel(paymentStatus, payment.status),
      Metodo: payment.method || payment.provider || 'Sin metodo',
      Monto: currency(payment.amount),
      Descuento: currency(payment.discount_amount || 0)
    }));
  }, [payments]);

  const productRows = useMemo(() => {
    const map = new Map();

    orderDetails.forEach((order) => {
      (order.items || []).forEach((item) => {
        const key = item.product_name || 'Producto sin nombre';
        const current = map.get(key) || {
          Producto: key,
          Unidades: 0,
          Ventas: 0,
          Descuentos: 0,
          Pedidos: 0
        };

        current.Unidades += Number(item.quantity || 0);
        current.Ventas += Number(item.total_amount || 0);
        current.Descuentos += Number(item.discount_amount || 0);
        current.Pedidos += 1;
        map.set(key, current);
      });
    });

    return Array.from(map.values())
      .sort((a, b) => b.Unidades - a.Unidades)
      .map((row) => ({
        ...row,
        Ventas: currency(row.Ventas),
        Descuentos: currency(row.Descuentos)
      }));
  }, [orderDetails]);

  const orderStatusRows = useMemo(() => {
    const map = new Map();

    orders.forEach((order) => {
      const status = order.status || 'sin_estado';
      const current = map.get(status) || {
        Estado: statusLabel(orderStatus, status),
        Pedidos: 0,
        Total: 0
      };
      current.Pedidos += 1;
      current.Total += Number(order.total_amount || 0);
      map.set(status, current);
    });

    return Array.from(map.values()).map((row) => ({
      ...row,
      Total: currency(row.Total)
    }));
  }, [orders]);

  const appointmentColumns = [
    { key: 'Fecha', label: 'Fecha' },
    { key: 'Cliente', label: 'Cliente' },
    { key: 'Mascota', label: 'Mascota' },
    { key: 'Servicio', label: 'Servicio' },
    { key: 'Profesional', label: 'Profesional' },
    { key: 'Estado', label: 'Estado' }
  ];

  const paymentColumns = [
    { key: 'Fecha', label: 'Fecha' },
    { key: 'Cliente', label: 'Cliente' },
    { key: 'Concepto', label: 'Concepto' },
    { key: 'Metodo', label: 'Metodo' },
    { key: 'Monto', label: 'Monto' },
    {
      key: 'Estado',
      label: 'Estado',
      render: (row) => {
        const raw = payments.find((payment) => statusLabel(paymentStatus, payment.status) === row.Estado)?.status;
        return <StatusBadge {...getStatusConfig(paymentStatus, raw || row.Estado)} />;
      }
    }
  ];

  const productColumns = [
    { key: 'Producto', label: 'Producto' },
    { key: 'Unidades', label: 'Unidades' },
    { key: 'Pedidos', label: 'Pedidos' },
    { key: 'Ventas', label: 'Ventas' },
    { key: 'Descuentos', label: 'Descuentos' }
  ];

  const statusColumns = [
    { key: 'Estado', label: 'Estado' },
    { key: 'Pedidos', label: 'Pedidos' },
    { key: 'Total', label: 'Total' }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reportes"
        description="Indicadores y descargas operacionales del periodo seleccionado."
        actions={<Button icon={RefreshCw} variant="secondary" onClick={loadReports}>Actualizar</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <FormField label="Desde">
            <input type="date" className={inputClassName} value={from} onChange={(event) => setFrom(event.target.value)} />
          </FormField>
          <FormField label="Hasta">
            <input type="date" className={inputClassName} value={to} onChange={(event) => setTo(event.target.value)} />
          </FormField>
          <div className="flex items-end">
            <Button variant="secondary" onClick={loadReports} className="w-full">Aplicar periodo</Button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard title="Ventas pagadas" value={currency(totalSales)} icon={DollarSign} tone="green" />
        <StatCard title="Pendiente de pago" value={currency(pendingSales)} icon={DollarSign} tone="amber" />
        <StatCard title="Citas del periodo" value={appointments.length} icon={CalendarDays} />
        <StatCard title="Clientes nuevos" value={newCustomers.length} icon={Users} tone="slate" />
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <Button
            icon={Download}
            variant="secondary"
            disabled={!paymentReportRows.length}
            onClick={() => downloadCsv(reportName('reporte-financiero', from, to), paymentReportRows)}
          >
            Descargar financiero
          </Button>
          <Button
            icon={Download}
            variant="secondary"
            disabled={!appointmentReportRows.length}
            onClick={() => downloadCsv(reportName('agenda-clientes', from, to), appointmentReportRows)}
          >
            Descargar agenda
          </Button>
          <Button
            icon={Download}
            variant="secondary"
            disabled={!productRows.length}
            onClick={() => downloadCsv(reportName('productos-vendidos', from, to), productRows)}
          >
            Descargar productos
          </Button>
        </div>
      </section>

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-950">Pagos del periodo</h2>
                <p className="text-sm text-gray-500">Pagado: {currency(totalSales)} · Pendiente: {currency(pendingSales)}</p>
              </div>
              <Button size="sm" variant="secondary" icon={Download} disabled={!paymentReportRows.length} onClick={() => downloadCsv(reportName('reporte-financiero', from, to), paymentReportRows)}>CSV</Button>
            </div>
            <Table columns={paymentColumns} data={paymentReportRows} emptyMessage="Sin pagos en el periodo" />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-950">Citas y clientes</h2>
                <p className="text-sm text-gray-500">{newCustomers.length} clientes nuevos en el periodo</p>
              </div>
              <Button size="sm" variant="secondary" icon={Download} disabled={!appointmentReportRows.length} onClick={() => downloadCsv(reportName('agenda-clientes', from, to), appointmentReportRows)}>CSV</Button>
            </div>
            <Table columns={appointmentColumns} data={appointmentReportRows} emptyMessage="Sin citas en el periodo" />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-950">Productos mas vendidos</h2>
                <p className="text-sm text-gray-500">Pedidos del periodo: {orders.length} · Total tienda: {currency(orderTotal)}</p>
              </div>
              <Button size="sm" variant="secondary" icon={Download} disabled={!productRows.length} onClick={() => downloadCsv(reportName('productos-vendidos', from, to), productRows)}>CSV</Button>
            </div>
            <Table columns={productColumns} data={productRows} emptyMessage="Sin productos vendidos" />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-950">Pedidos por estado</h2>
            </div>
            <Table columns={statusColumns} data={orderStatusRows} emptyMessage="Sin pedidos en el periodo" />
          </section>
        </div>
      )}
    </div>
  );
};

export default Reportes;
